import { useTrackEvent } from '@/services/analytics';
import { useSetUserFlags } from '@/features/user-flags/hooks';
import { servicesApi } from '@/services/api';
import { useAuth } from '@/services/auth';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { atom } from 'nanostores';
import { useState } from 'react';
const isProduction = __CIO_ENV__ === 'production';

const TOKEN_LIFETIME_REFRESH_THRESHOLD = 0.8;

const _isInitialized = atom(false);
const _currentToken = atom<string | null>(null);
const _unreadChatCount = atom(0);
const _zendeskTokenState = atom<{
  token: string;
  isHandoverAllowed: boolean;
  tokenLifetimeMinutes: number;
  createdAt: number;
} | null>(null);

/**
 * This function handles the zendesk token
 *
 * We have three scenarios:
 * 1. No token exists: Fetch a new token from the API along with its configuration.
 * 2. Token exists but is close to expiration: Refresh the token using the API.
 * 3. Token exists and is still valid: Use the existing token.
 */
async function getZendeskAccess(
  accountId: number,
  userId: number,
): Promise<{
  token: string;
  isHandoverAllowed: boolean;
  tokenLifetimeMinutes: number;
  createdAt: number;
}> {
  const zendeskTokenState = _zendeskTokenState.get();
  // If no token exists, fetch a new one
  if (zendeskTokenState == null) {
    const response = await servicesApi.getZendeskChatAccessToken({
      params: { accountId, userId },
    });

    const newState = {
      token: response.token,
      isHandoverAllowed: response.is_handover_allowed,
      tokenLifetimeMinutes: response.token_lifetime_minutes,
      createdAt: Date.now(),
    };

    _zendeskTokenState.set(newState);

    return newState;
  }

  // If the token is close to expiration, refresh it
  const msSinceCreation = Date.now() - zendeskTokenState.createdAt;
  if (msSinceCreation >= zendeskTokenState.tokenLifetimeMinutes * 60 * 1000 * TOKEN_LIFETIME_REFRESH_THRESHOLD) {
    const response = await servicesApi.refreshZendeskChatToken({
      params: { accountId, userId },
      body: undefined,
    });

    const newState = {
      token: response.token,
      isHandoverAllowed: zendeskTokenState.isHandoverAllowed,
      tokenLifetimeMinutes: zendeskTokenState.tokenLifetimeMinutes,
      createdAt: Date.now(),
    };

    _zendeskTokenState.set(newState);

    return newState;
  }

  // If the token is still valid, return it
  return zendeskTokenState;
}

/**
 * A react query that connects the zendesk chat widget to the user
 * and sets up the necessary event listeners.
 */
function useZendeskAccess() {
  const auth = useAuth();
  return useQuery({
    queryKey: ['zendeskAccess'],
    queryFn: async () => {
      const access = await getZendeskAccess(auth.account.id, auth.user.id);

      if (_isInitialized.get() === false) {
        const conversationTags: string[] = [];
        if (access.isHandoverAllowed !== true) {
          conversationTags.push('maa-ai-only');
        }

        if (isProduction === false) {
          conversationTags.push('internal-test');
        }

        window.zE?.('messenger:set', 'conversationTags', conversationTags);
        window.zE?.('messenger:on', 'unreadMessages', (count: number) => {
          _unreadChatCount.set(count);
        });
        window.zE?.('messenger', 'show');
        _isInitialized.set(true);

        return access;
      }

      return access;
    },
    enabled: auth.account.is_masq === false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    staleTime: Infinity,
  });
}

export function useSupportChatSparkles() {
  const auth = useAuth();
  const { mutate: mutateUserFlags } = useSetUserFlags();
  const [shouldShowSparkle, setShowSparkle] = useState(auth.user.user_flags?.show_chat_sparkles ?? false);

  const disableSparkle = () => {
    if (shouldShowSparkle !== true) {
      return;
    }

    setShowSparkle(false);
    mutateUserFlags({
      accountId: auth.account.id,
      userId: auth.user.id,
      flags: {
        show_chat_sparkles: false,
      },
    });
  };

  return { shouldShowSparkle, disableSparkle };
}

let draftMessageInterval: number | undefined = undefined;
export function useSupportChat() {
  const auth = useAuth();
  const { data, refetch, isFetching } = useZendeskAccess();
  const unreadCount = useStore(_unreadChatCount);
  const track = useTrackEvent();
  const snackbars = useSnackbars();

  async function openChat(message?: string) {
    if (data == null) {
      return;
    }

    async function doOpening() {
      const response = await refetch();
      if (response.data == null) {
        return;
      }

      const { token } = response.data;

      // when the token is refreshed, we need to re-login the user in the chat widget
      if (_currentToken.get() !== token) {
        _currentToken.set(token);
        await new Promise<void>((resolve) => {
          window.zE?.('messenger', 'loginUser', (callback: (token: string) => void) => {
            callback(token);
            resolve();
          });
        });
      }

      window.zE?.('messenger', 'open');
      _unreadChatCount.set(0);
      track('Support Chat Clicked', { 'Account Id': auth.account.id });

      // there is no way to set a draft of a message in the Zendesk chat widget,
      // so we need to set the message in the textarea directly
      // this is a hacky way to do it, but it works
      // we wait for the iframe to be loaded and then set the value of the textarea
      if (message != null) {
        let attempts = 0;
        window.clearInterval(draftMessageInterval);
        draftMessageInterval = window.setInterval(() => {
          // this is convoluted, but we need to set the value in this
          // specific way for zendesk to recognize it as user input
          const iframe = document.querySelector<HTMLIFrameElement>('iframe[name="Messaging window"]');
          const textarea = iframe?.contentDocument?.querySelector('textarea');

          if (textarea != null) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLTextAreaElement.prototype,
              'value',
            )?.set;

            nativeInputValueSetter?.call(textarea, message);

            const event = new Event('input', { bubbles: true });
            textarea.dispatchEvent(event);
            window.clearInterval(draftMessageInterval);
          } else {
            attempts += 1;
            if (attempts >= 20) {
              window.clearInterval(draftMessageInterval);
              // eslint-disable-next-line no-console
              console.warn('Failed to set message in support chat after 2 seconds');
            }
          }
        }, 100);
      }
    }

    // show a warning to our developers about touching production Zendesk data
    if (!isProduction) {
      snackbars.create({
        message:
          'Support chat uses production Zendesk account, but you are in development mode. If you continue, you will mess with production data and our stats. Please proceed with caution.',
        action: {
          label: 'Continue',
          onClick: () => {
            void doOpening();
          },
        },
      });
    } else {
      await doOpening();
    }
  }

  return {
    openChat,
    isLoading: isFetching,
    unreadCount,
    isVisible: data != null,
    isHandoverAllowed: data?.isHandoverAllowed === true,
  };
}
